import * as Sentry from '@sentry/browser'
import { localStorageApi } from '~/plugins/helpers/localstorage.client'
import { ReportPageT } from '~/types/reportTypes'

// Cache key constants
export const REFETCH_TEAM_REPO_GROUP_LIST = 'refetch-team-repo-group-list'
export const REFETCH_TEAM_REPO_LIST = 'refetch-team-repo-list'
export const REFETCH_TEAM_REPO_GROUP_OVERVIEW = 'refetch-team-repo-group-overview'
export const REFETCH_TEAM_REPO_GROUP_REPORTS = 'refetch-team-repo-group-reports'

export type ReportCacheStoreT = Partial<Record<ReportPageT, boolean>>

// Add input validation and error handling for localStorage operations
export function safeSetLocalStorage(store: string, key: string, value: unknown) {
  if (!store || !key) {
    Sentry.captureMessage('safeSetLocalStorage: Invalid store or key', {
      level: 'warning',
      extra: { store, key }
    })
    return
  }
  try {
    localStorageApi.set(store, key, value)
  } catch (e) {
    Sentry.captureException(e, {
      extra: { store, key, value }
    })
  }
}

export function setAllCacheBustKeys(provider: string, owner: string, groupSlug: string) {
  const teamRepoKey = `${provider}-${owner}`
  const teamRepoGroupListKey = `${provider}-${owner}-${groupSlug}`

  safeSetLocalStorage(teamRepoKey, REFETCH_TEAM_REPO_GROUP_LIST, true)
  safeSetLocalStorage(teamRepoKey, REFETCH_TEAM_REPO_LIST, true)
  safeSetLocalStorage(teamRepoGroupListKey, REFETCH_TEAM_REPO_GROUP_OVERVIEW, true)
  safeSetLocalStorage(teamRepoGroupListKey, REFETCH_TEAM_REPO_GROUP_REPORTS, {
    [ReportPageT.OWASP_TOP_10]: true,
    [ReportPageT.SANS_TOP_25]: true,
    [ReportPageT.MISRA_C]: true,
    [ReportPageT.DISTRIBUTION]: true,
    [ReportPageT.CODE_COVERAGE]: true,
    [ReportPageT.CODE_HEALTH_TREND]: true,
    [ReportPageT.ISSUES_AUTOFIXED]: true,
    [ReportPageT.ISSUES_PREVENTED]: true
  })
}

export function setReportCacheBustKey(
  {
    provider,
    owner,
    groupSlug,
    reportPage
  }: {
    provider: string
    owner: string
    groupSlug: string
    reportPage: ReportPageT
  },
  newValue: boolean
) {
  if (
    provider == null ||
    provider.trim().length === 0 ||
    owner == null ||
    owner.trim().length === 0 ||
    groupSlug == null ||
    groupSlug.trim().length === 0 ||
    reportPage == null
  ) {
    Sentry.captureMessage('setReportCacheBustKey: Invalid input', {
      level: 'warning',
      extra: { provider, owner, groupSlug, reportPage }
    })
    return
  }
  const key = `${provider}-${owner}-${groupSlug}`
  let currentValue: ReportCacheStoreT = {}
  try {
    currentValue = localStorageApi.get(key, REFETCH_TEAM_REPO_GROUP_REPORTS) || {}
    currentValue[reportPage] = newValue
    safeSetLocalStorage(key, REFETCH_TEAM_REPO_GROUP_REPORTS, currentValue)
  } catch (e) {
    Sentry.captureException(e, {
      extra: { key, reportPage, newValue }
    })
  }
}

// Utility to get and clear cache bust flag
export function getReportCacheBustFlag(
  provider: string,
  owner: string,
  groupSlug: string,
  reportPage: ReportPageT
): boolean {
  if (!provider || !owner || !groupSlug || !reportPage) return false
  try {
    const key = `${provider}-${owner}-${groupSlug}`
    const cacheObj: ReportCacheStoreT =
      localStorageApi.get(key, REFETCH_TEAM_REPO_GROUP_REPORTS) || {}
    return Boolean(cacheObj[reportPage])
  } catch (e) {
    return false
  }
}

export function clearReportCacheBustFlag(
  provider: string,
  owner: string,
  groupSlug: string,
  reportPage: ReportPageT
) {
  if (!provider || !owner || !groupSlug || !reportPage) {
    Sentry.captureMessage('clearReportCacheBustFlag: Invalid input', {
      level: 'warning',
      extra: { provider, owner, groupSlug, reportPage }
    })
    return
  }
  try {
    const key = `${provider}-${owner}-${groupSlug}`
    const cacheObj: ReportCacheStoreT =
      localStorageApi.get(key, REFETCH_TEAM_REPO_GROUP_REPORTS) || {}
    cacheObj[reportPage] = false
    safeSetLocalStorage(key, REFETCH_TEAM_REPO_GROUP_REPORTS, cacheObj)
  } catch (e) {
    Sentry.captureException(e, {
      extra: { provider, owner, groupSlug, reportPage }
    })
  }
}

export function getGroupOverviewCacheBustFlag(
  provider: string,
  owner: string,
  groupSlug: string
): boolean {
  if (!provider || !owner || !groupSlug) {
    Sentry.captureMessage('getGroupOverviewCacheBustFlag: Invalid input', {
      level: 'warning',
      extra: { provider, owner, groupSlug }
    })
    return false
  }
  try {
    const key = `${provider}-${owner}-${groupSlug}`
    return Boolean(localStorageApi.get(key, REFETCH_TEAM_REPO_GROUP_OVERVIEW))
  } catch (e) {
    Sentry.captureException(e, {
      extra: { provider, owner, groupSlug }
    })
    return false
  }
}

export function clearGroupOverviewCacheBustFlag(
  provider: string,
  owner: string,
  groupSlug: string
) {
  if (!provider || !owner || !groupSlug) {
    Sentry.captureMessage('clearGroupOverviewCacheBustFlag: Invalid input', {
      level: 'warning',
      extra: { provider, owner, groupSlug }
    })
    return
  }
  try {
    const key = `${provider}-${owner}-${groupSlug}`
    safeSetLocalStorage(key, REFETCH_TEAM_REPO_GROUP_OVERVIEW, false)
  } catch (e) {
    Sentry.captureException(e, {
      extra: { provider, owner, groupSlug }
    })
  }
}
